import { displayUnitOf } from "@heraion/shared";
import { useMemo } from "react";

import { ExercisePickerMount } from "../ExercisePickerMount";
import {
  createSessionResultsCache,
  resolveTheme,
  type ProgressDeps,
  type SessionResultsSnapshot,
  type UseSessionResultsOptions,
} from "@heraion/ui";
import {
  EMPTY_PAIN_MAP,
  Profile,
  type PainMap,
  type TimedSessionResult,
} from "@heraion/shared";

import { apiFetch } from "../api";
import { exerciseCatalog } from "../logger/exercisesCache";
import { lsCache } from "../lsCache";
import {
  useProfileDoc,
  type UseProfileDoc,
} from "../useProfileDoc";
import { useThemePalette } from "../theme";

// One single-flight session-results cache shared by every SELF surface wired
// through this hook: the Progress panels warm it, and the Goals editor
// (TargetsEditor) paints its reference numbers straight from it (getCached is
// synchronous) instead of after a spinner. Reusing the SAME instance avoids a
// cold-paint regression and two caches that could diverge on save. Mirror of
// mobile's apps/mobile/src/progress/progressDeps.ts. The Home (PlanScreen: the
// meter, the composer, the widgets) reads it too since CN-self-results-cache.
const resultsCache = createSessionResultsCache();

// RW-18: the Logger's `invalidateSessionResults` host dep — drops this SAME
// cache after a save so Progress refetches fresh, matching mobile's
// apps/mobile/src/progress/sessionResultsCache.ts's invalidate. Exported as
// a plain function (not a hook) because useSaveWorkout's caller has no
// component to hook from — same reasoning as mobile's split-out module.
export const invalidateSelfSessionResults = (): void => resultsCache.invalidate();

// Cold-boot disk SWR, localStorage twin of mobile's AsyncStorage
// resultsPersist (apps/mobile/src/progress/progressDeps.ts)
// — same storage keys, `lsCache` is a drop-in for `asyncStorageResourceCache`
// (both `{read,write}` over a single JSON blob).
const resultsPersist = lsCache<TimedSessionResult[]>("progress/sessionResults");

// Module single-flight for the stale disk read so the panels that mount
// together share ONE localStorage round-trip, mirroring mobile's
// staleInFlight (apps/mobile/src/progress/progressDeps.ts:40-50).
let staleInFlight: Promise<SessionResultsSnapshot | null> | null = null;
function readStaleResults(): Promise<SessionResultsSnapshot | null> {
  if (!staleInFlight) {
    staleInFlight = Promise.all([resultsPersist.read(), exerciseCatalog.loadCached()])
      .then(([results, catalog]) =>
        results && catalog ? { results, catalog } : null,
      )
      .catch(() => null);
  }
  return staleInFlight;
}

function persistResults(results: TimedSessionResult[]): void {
  // A fresh fetch supersedes any stale disk snapshot from this run.
  staleInFlight = null;
  void resultsPersist.write(results);
}

// RW-18: the Logger's `sessionResultsDeps` host dep (hostDeps.ts —
// "useSessionResults' own deps bundle") — the SAME resultsCache instance
// above, so a save invalidates the identical cache the in-workout meter and
// the Progress panels read, plus the cold-boot localStorage SWR above
// (mirrors mobile's apps/mobile/src/progress/progressDeps.ts's plain
// (non-hook) export).
export const selfSessionResultsDeps: UseSessionResultsOptions = {
  apiFetch,
  catalog: exerciseCatalog,
  cache: resultsCache,
  readStale: readStaleResults,
  persistResults,
};

// The SELF ProgressDeps — my own training over the unscoped apiFetch, not an
// athlete I coach (that lives in AthleteDetailScreen with a per-sub scoped cache).
// The minimal fields the shared Tier-1 panels + TargetsEditor consume;
// ProgressScreen layers its PG-1 extras (pinnedProgram / prescription / figure)
// on top of this base.
export function useSelfProgressDeps(): ProgressDeps {
  const { doc } = useProfileDoc();
  return useSelfProgressDepsFor(doc);
}

/** The same deps from a profile doc the caller already holds: each
 *  useProfileDoc() is its own GET, and the Home has one open already. */
export function useSelfProgressDepsFor(doc: UseProfileDoc["doc"]): ProgressDeps {
  const { dark, family } = useThemePalette();
  const units = displayUnitOf(doc);
  const weekStartDay = doc?.["weekStartDay"] as number | undefined;
  // BodyScreen's PainMapPicker (lifted 2026-08-16) writes painMap into this
  // SAME profile doc via savePatch — read it back the way BodyScreen.tsx:45
  // does, mirroring mobile's `profile.painMap ?? EMPTY_PAIN_MAP`.
  const painMap =
    (doc?.["painMap"] as PainMap | undefined) ?? EMPTY_PAIN_MAP;
  // AP-9: the gap proposal lists what is doable at the default place.
  const rawPlace = doc?.["defaultLocation"];
  const rawDeclared = doc?.["locationUnavailable"];
  const trainingPlace = useMemo(() => {
    const place = Profile.shape.defaultLocation.safeParse(rawPlace);
    const declared = Profile.shape.locationUnavailable.safeParse(rawDeclared);
    return {
      place: place.success ? (place.data ?? null) : null,
      unavailable: declared.success ? declared.data : undefined,
    };
  }, [rawPlace, rawDeclared]);
  return useMemo<ProgressDeps>(
    () => ({
      apiFetch,
      catalog: exerciseCatalog,
      // PG-24 — the same mount TimersScreen already uses, so the "compare
      // with" control exists on web too. ⛔ Without this the button would be
      // absent on web only, which is the silent lift gap `rnw-strangler §8`
      // exists to stop — and the mount was already here, so there was nothing
      // to park.
      ExercisePickerMount,
      theme: resolveTheme(dark, family),
      units,
      painMap,
      resultsCache,
      readStaleResults,
      persistResults,
      isDark: dark,
      weekStartDay,
      trainingPlace,
    }),
    [dark, family, units, weekStartDay, painMap, trainingPlace],
  );
}
